import React, { useEffect, useState } from 'react';
import { FiSearch } from 'react-icons/fi';
import { Link, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import { IoClose } from 'react-icons/io5';
import 'react-toastify/dist/ReactToastify.css';
import 'react-datepicker/dist/react-datepicker.css';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;
const API_URL = `${BQ_BASE_URL}/bq/api/rooms/search`;
const AVAILABILITY_API_URL = `${BQ_BASE_URL}/bq/api/availability`;

const RoomManagement = () => {
  const [roomData, setRoomData] = useState({});
  const [availableRoomTypes, setAvailableRoomTypes] = useState([]);
  const navigate = useNavigate();

  const [isPopOpen, setIsPopOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [updatedRoom, setUpdatedRoom] = useState(selectedRoom);
  const [roomTypes, setRoomTypes] = useState([]);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [roomStatus, setRoomStatus] = useState('all');
  const [selectedRoomType, setSelectedRoomType] = useState('all');
  const [onlyAvailableRooms, setOnlyAvailableRooms] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newRoom, setNewRoom] = useState({
    room_no: '',
    room_type: '',
    status: 'Available',
  });

  useEffect(() => {
    const fetchRoomTypes = async () => {
      try {
        const response = await fetch(`${BQ_BASE_URL}/bq/api/roomtypes`);
        const data = await response.json();
        setRoomTypes(data);
      } catch (error) {
        console.error('Error fetching room types:', error);
      }
    };

    fetchRoomTypes();
  }, []);

  const handleAddRoom = async () => {
    const roomData = {
      roomnumber: newRoom.room_no,
      status: newRoom.status,
      roomtypename: newRoom.room_type,
    };

    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/rooms`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(roomData),
      });

      const responseData = await response.json();

      if (response.ok) {
        toast.success('Room added successfully!');
        setIsModalOpen(false);

        const { roomid, roomnumber, status, roomtype } = responseData;

        setRoomData((prevData) => {
          const updatedRoomData = { ...prevData };
          const roomTypeName = roomtype.roomtypename;
          const roomStatus = status.toLowerCase();

          if (!updatedRoomData[roomTypeName]) {
            updatedRoomData[roomTypeName] = {
              available: [],
              occupied: [],
              reserved: [],
              'Under Maintenance': [],
            };
          }

          updatedRoomData[roomTypeName][roomStatus].push({
            roomid,
            roomnumber,
            status,
            roomtype: roomTypeName,
          });

          return updatedRoomData;
        });

        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        const errorMessage = responseData.detail || 'Failed to add room.';
        toast.error(errorMessage);
        console.error('Failed to add room:', errorMessage);
      }
    } catch (error) {
      console.error('Error adding room:', error);
      toast.error('Error adding room.');
    }
  };

  useEffect(() => {
    setUpdatedRoom(selectedRoom);
  }, [selectedRoom]);

  const handleUpdate = async () => {
    // Prepare data according to API requirements
    const roomData = {
      roomid: selectedRoom.roomid,
      status: selectedRoom.status,
      auto_assign_room: selectedRoom.auto_assign_room || false,
    };

    console.log('Sending update data:', roomData);

    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/update-room-status/`, {
        method: 'PUT',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(roomData),
      });

      const responseData = await response.json();
      console.log('Update response:', responseData);

      if (response.ok) {
        toast.success('Room updated successfully.');
        modalClose();

        // Update local state with the new values
        setRoomData((prevData) => {
          const updatedRoomData = { ...prevData };

          Object.keys(updatedRoomData).forEach((category) => {
            Object.keys(updatedRoomData[category]).forEach((status) => {
              const rooms = updatedRoomData[category][status];
              const roomIndex = rooms.findIndex((room) => room.roomid === selectedRoom.roomid);
              if (roomIndex !== -1) {
                // Update both status and auto_assign_room
                updatedRoomData[category][status][roomIndex].status = selectedRoom.status;
                updatedRoomData[category][status][roomIndex].auto_assign_room =
                  selectedRoom.auto_assign_room;

                // Move room to correct status category if status changed
                if (status.toLowerCase() !== selectedRoom.status.toLowerCase()) {
                  const roomToMove = updatedRoomData[category][status][roomIndex];
                  updatedRoomData[category][status].splice(roomIndex, 1);

                  // Add to new status category
                  const newStatus = selectedRoom.status.toLowerCase();
                  if (!updatedRoomData[category][newStatus]) {
                    updatedRoomData[category][newStatus] = [];
                  }
                  updatedRoomData[category][newStatus].push(roomToMove);
                }
              }
            });
          });

          return updatedRoomData;
        });
      } else {
        console.error('Failed to update room:', responseData);
        toast.error('Failed to update room: ' + (responseData.detail || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error updating room:', error);
      toast.error('Error updating room: ' + error.message);
    }
  };

  const handleDeleteRoom = async (roomnumber) => {
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/rooms/${roomnumber}`, {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
        },
      });

      if (response.ok) {
        toast.success('Room deleted successfully');
        setIsPopOpen(false);
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        const result = await response.json();
        console.error('Delete failed:', result);
        toast.error(result.detail || 'Failed to delete room');
      }
    } catch (error) {
      console.error('Error deleting room:', error);
      toast.error('Error deleting room');
    }
  };

  // Fetch room details and available room types
  useEffect(() => {
    const fetchRoomDetails = async () => {
      try {
        const response = await fetch(API_URL);
        const data = await response.json();

        if (data && data.rooms && typeof data.rooms === 'object') {
          const formattedRoomData = {};

          Object.keys(data.rooms).forEach((category) => {
            formattedRoomData[category] = {};
            Object.keys(data.rooms[category]).forEach((status) => {
              let rooms = data.rooms[category][status];

              if (rooms && rooms.rooms && Array.isArray(rooms.rooms)) {
                formattedRoomData[category][status] = rooms.rooms;
              } else if (Array.isArray(rooms)) {
                formattedRoomData[category][status] = rooms;
              } else if (typeof rooms === 'object') {
                console.warn(
                  `Expected array for category "${category}" and status "${status}", but got:`,
                  rooms
                );
                formattedRoomData[category][status] = Object.values(rooms);
              } else {
                console.warn(
                  `Unexpected type for rooms in category "${category}" and status "${status}"`
                );
              }
            });
          });

          setRoomData(formattedRoomData);
        } else {
          console.warn('Expected rooms data in an object format, but got:', data);
        }
      } catch (error) {
        console.error('Error fetching room details:', error);
      }
    };

    const fetchAvailableRoomTypes = async () => {
      try {
        const response = await fetch(AVAILABILITY_API_URL);
        const data = await response.json();
        setAvailableRoomTypes(data.available_room_types);
      } catch (error) {
        console.error('Error fetching available room types:', error);
        setAvailableRoomTypes([]);
      }
    };

    fetchRoomDetails();
    fetchAvailableRoomTypes();
  }, []);

  // Filter rooms based on search criteria
  const filteredRooms = Object.keys(roomData).map((roomType) => {
    const rooms = Object.keys(roomData[roomType]).flatMap((status) =>
      roomData[roomType][status].filter((room) => {
        const matchesSearch =
          (room?.baseprice?.toString()?.includes(searchTerm) ?? false) ||
          (room?.roomnumber?.toString()?.includes(searchTerm) ?? false);

        const matchesStatus = roomStatus === 'all' || room.status.toLowerCase() === roomStatus;
        const matchesRoomType =
          selectedRoomType === 'all' ||
          room.room_type.toLowerCase() === selectedRoomType.toLowerCase();

        const matchesAvailability =
          !onlyAvailableRooms || room.status.toLowerCase() === 'available';

        return matchesSearch && matchesStatus && matchesRoomType && matchesAvailability;
      })
    );

    const availableCount = rooms.filter((room) => room.status.toLowerCase() === 'available').length;
    const occupiedCount = rooms.filter((room) => room.status.toLowerCase() === 'occupied').length;
    const reservedCount = rooms.filter((room) => room.status.toLowerCase() === 'reserved').length;
    const maintenanceCount = rooms.filter(
      (room) => room.status.toLowerCase() === 'under maintenance'
    ).length;

    return {
      roomType,
      availableCount,
      occupiedCount,
      reservedCount,
      maintenanceCount,
      rooms,
    };
  });

  const handleClearFilters = () => {
    setSearchTerm('');
    setRoomStatus('all');
    setSelectedRoomType('all');
    setOnlyAvailableRooms(false);
  };

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewRoom((prevRoom) => ({ ...prevRoom, [name]: value }));
  };

  const handleStatusChange = (e) => {
    const { name, value } = e.target;
    setNewRoom((prevRoom) => ({
      ...prevRoom,
      [name]: value,
    }));
  };

  const openModal = (room) => {
    setSelectedRoom(room);
    setIsPopOpen(true);
  };

  const modalClose = () => {
    setIsPopOpen(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSelectedRoom((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  return (
    <div className="p-6 bg-gray-100 ">
      {/* ... (Your existing header code remains the same) ... */}
      <header className="flex justify-between items-center mb-3 bg-white p-3">
        <h1 className="text-black text-xl font-bold ml-2">Room Inventory</h1>
        <div className="flex space-x-6">
          <button
            onClick={() => navigate('/dashboard/mdm/room-management/overview')}
            className="btn"
          >
            Overview
          </button>
          <button
            onClick={() => navigate('/dashboard/mdm/room-management/rooms')}
            className="btn  text-black font-bold"
          >
            Room Management
          </button>
        </div>
      </header>

      <h1 className="text-2xl font-bold text-center text-black">Room Management</h1>
      <h1 className="text-sm font-poppins font-semibold text-center mb-1 text-gray-600">
        Efficient room management ensures seamless booking, maintenance, and comfort, providing
        guests with an exceptional experience throughout their stay.
      </h1>
      <hr className="border border-gray-400 w-1/2 mx-auto mb-7" />

      {/* Filters Section */}
      <div className="flex justify-between items-center gap-2 mb-8 text-sm w-full">
        {/* ... (Your existing filter code remains the same) ... */}
        <div className="w-full mb-4 sm:mb-0 relative">
          <input
            type="text"
            className="w-full px-2 py-2 pl-10 border border-gray-300 rounded-md"
            placeholder="Find a room"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <FiSearch
            size={16}
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-600"
          />
        </div>

        <div className="w-full mb-4 sm:mb-0">
          <select
            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            value={roomStatus}
            onChange={(e) => setRoomStatus(e.target.value)}
          >
            <option value="all" disabled>
              Rooms Status
            </option>
            <option value="available">Available</option>
            <option value="reserved">Reserved</option>
            <option value="occupied">Occupied</option>
            <option value="under maintenance">Under Maintenance</option>
          </select>
        </div>
        <div className="w-full mb-4 sm:mb-0">
          <select
            className="w-full px-4 py-2 border border-gray-300 rounded-lg outline-none"
            value={selectedRoomType}
            onChange={(e) => {
              setSelectedRoomType(e.target.value);
            }}
          >
            <option value="all" disabled>
              All Room Types
            </option>
            {roomData &&
              Object.keys(roomData).map((roomType) => (
                <option key={roomType} value={roomType}>
                  {roomType}
                </option>
              ))}
          </select>
        </div>

        <div>
          <button
            onClick={handleOpenModal}
            className="px-10 py-2 bg-black text-white rounded-lg hover:bg-black whitespace-nowrap"
          >
            Add Room
          </button>
        </div>

        <Link to={'/dashboard/mdm/room-management/rooms/addroomtype'}>
          <div>
            <button className="px-6 py-2 bg-black text-white rounded-lg hover:bg-black whitespace-nowrap">
              Add Room Type
            </button>
          </div>
        </Link>

        <div className="w-full mr-4">
          <button
            onClick={handleClearFilters}
            className="w-full px-10 py-2 bg-red-500 text-white rounded-lg hover:text-black"
          >
            Clear All
          </button>
        </div>
      </div>

      <div className="w-full mb-4">
        {/* ... (Your existing room cards code remains the same) ... */}
        {filteredRooms.length > 0 ? (
          filteredRooms
            .filter(({ roomType }) => selectedRoomType === roomType)
            .map(
              ({
                roomType,
                availableCount,
                occupiedCount,
                reservedCount,
                maintenanceCount,
                rooms,
              }) => (
                <div key={roomType} className="mb-2">
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    <div className="bg-white p-5 rounded-lg shadow-md">
                      <h3 className="font-semibold text-lg text-gray-700">Total Rooms</h3>
                      <p className="text-3xl font-bold text-gray-800">{rooms.length}</p>
                    </div>
                    <div className="bg-white p-5 rounded-lg shadow-md">
                      <h3 className="font-semibold text-lg text-gray-700">Available Rooms</h3>
                      <p className="text-3xl font-bold text-gray-800">{availableCount}</p>
                    </div>
                    <div className="bg-white p-5 rounded-lg shadow-md">
                      <h3 className="font-semibold text-lg text-gray-700">Reserved Rooms</h3>
                      <p className="text-3xl font-bold text-gray-800">{reservedCount}</p>
                    </div>
                    <div className="bg-white p-5 rounded-lg shadow-md">
                      <h3 className="font-semibold text-lg text-gray-700">Occupied Rooms</h3>
                      <p className="text-3xl font-bold text-gray-800">{occupiedCount}</p>
                    </div>
                    <div className="bg-white p-5 rounded-lg shadow-md">
                      <h3 className="font-semibold text-lg text-gray-700">Under Maintenance</h3>
                      <p className="text-3xl font-bold text-gray-800">{maintenanceCount}</p>
                    </div>
                  </div>
                </div>
              )
            )
        ) : (
          <div className="text-center text-gray-700 mt-4 p-4 ">
            <p className="text-lg font-semibold text-gray-800">Loading...</p>
          </div>
        )}
      </div>

      {/* Room Table */}
      <div className="space-y-8 text-sm">
        {filteredRooms.map(({ roomType, rooms }) => {
          if (rooms.length === 0) return null;

          return (
            <div key={roomType}>
              <h2 className="bg-white text-black font-bold text-left p-3 text-base rounded-t-lg shadow-md">
                {roomType}
              </h2>

              <div className="overflow-x-auto rounded-b-lg">
                <table className="min-w-full table-auto bg-white">
                  <thead className="bg-gray-900 text-white">
                    <tr>
                      <th className="py-3 px-6 text-left font-medium w-1/6">Room No.</th>
                      <th className="py-3 px-6 text-left font-medium w-1/6">Room Type</th>
                      <th className="py-3 px-6 text-left font-medium w-1/6">Status</th>
                      <th className="py-3 px-6 text-left font-medium w-1/6">Price</th>
                      <th className="py-3 px-6 text-left font-medium w-1/6">Auto-Assign Room</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700 border border-gray-200">
                    {rooms.map((room, index) => (
                      <tr
                        key={room.id}
                        className={`${
                          index % 2 === 0 ? 'bg-white' : 'bg-gray-100'
                        } hover:bg-gray-200 cursor-pointer`}
                        onClick={() => openModal(room)}
                      >
                        <td className="py-3 px-6">{room.roomnumber}</td>
                        <td className="py-3 px-6">{room.room_type}</td>
                        <td className="py-3 px-6">{room.status}</td>
                        <td className="py-3 px-6">₹{room.baseprice}</td>
                        <td className="py-3 px-6">
                          {/* CHANGED: Added pointer-events-none to disable interaction */}
                          <label className="relative inline-flex items-center cursor-default pointer-events-none">
                            <input
                              type="checkbox"
                              className="sr-only peer"
                              checked={room.auto_assign_room}
                              readOnly
                              disabled // Added disabled attribute
                            />
                            <div className="w-10 h-6 bg-gray-300 rounded-full peer peer-checked:bg-black transition-all"></div>
                            <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform peer-checked:translate-x-full"></div>
                          </label>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-center items-center">
          <div className="relative bg-white p-6 rounded-md shadow-lg w-full max-w-lg text-xs">
            <button
              onClick={handleCloseModal}
              className="absolute top-4 right-3 text-red-500 hover:text-red-600"
            >
              <IoClose size={28} />
            </button>

            <h2 className="text-xl font-bold mb-4">Add New Room</h2>

            <div className="mb-1">
              <label className="block text-sm font-semibold mb-1">Room Number</label>
              <input
                type="text"
                name="room_no"
                placeholder="Enter room number."
                value={newRoom.room_no}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md text-sm"
              />
            </div>

            <div className="mb-1">
              <label className="block text-sm font-semibold mb-1">Room Type</label>
              <select
                name="room_type"
                value={newRoom.room_type}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="" disabled>
                  Select Room Type
                </option>
                {roomTypes.map((type) => (
                  <option key={type.roomtypename} value={type.roomtypename}>
                    {type.roomtypename}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-1">
              <label className="block text-sm font-semibold mb-1 text-sm">Status</label>
              <select
                name="status"
                value={newRoom?.status || 'Available'}
                onChange={handleStatusChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="Available">Available</option>
                <option value="Reserved">Reserved</option>
                <option value="Occupied">Occupied</option>
                <option value="Under Maintenance">Under Maintenance</option>
              </select>
            </div>

            <div className="flex justify-end space-x-4 text-sm mt-4">
              <button
                onClick={handleAddRoom}
                className="px-5 py-2 bg-black text-white rounded-md hover:bg-black"
              >
                Add Room
              </button>
            </div>
          </div>
        </div>
      )}

      {isPopOpen && selectedRoom && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex justify-center items-center z-50">
          <div className="relative bg-white p-8 rounded-lg shadow-lg w-1/3 text-sm text-black">
            <button
              onClick={modalClose}
              className="absolute top-4 right-3 text-red-500 hover:text-red-600"
            >
              <IoClose size={28} />
            </button>
            <h3 className="text-xl font-bold text-center">Room Details</h3>
            <p className="text-sm text-gray-500 mb-4 text-center font-semibold">
              Update the room details below, You can only change the status.
            </p>
            <div className="space-y-4 text-xs">
              <div>
                <label className="block text-black">Room Number</label>
                <input
                  type="text"
                  name="roomnumber"
                  value={selectedRoom.roomnumber}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border rounded-md cursor-not-allowed"
                  disabled
                />
              </div>
              <div>
                <label className="block text-black">Room Type</label>
                <input
                  type="text"
                  name="room_type"
                  value={selectedRoom.room_type}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border rounded-md cursor-not-allowed"
                  disabled
                />
              </div>
              <div>
                <label className="block text-black">Status</label>
                <select
                  name="status"
                  value={selectedRoom.status}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border rounded-md"
                >
                  <option value="Available">Available</option>
                  <option value="Reserved">Reserved</option>
                  <option value="Occupied">Occupied</option>
                  <option value="Under Maintenance">Under Maintenance</option>
                </select>
              </div>
              <div>
                <label className="block text-black">Price</label>
                <input
                  type="number"
                  name="baseprice"
                  value={selectedRoom.baseprice}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border rounded-md cursor-not-allowed"
                  disabled
                />
              </div>
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <label className="block text-black text-sm font-semibold">
                      Auto-Assign Room
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                      When enabled, this room is prioritized for automatic guest assignment.
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer ml-4">
                    <input
                      type="checkbox"
                      checked={selectedRoom.auto_assign_room}
                      onChange={(e) =>
                        setSelectedRoom((prev) => ({
                          ...prev,
                          auto_assign_room: e.target.checked,
                        }))
                      }
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-300 rounded-full peer peer-checked:bg-black transition-all"></div>
                    <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform peer-checked:translate-x-full"></div>
                  </label>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={handleUpdate}
                className="bg-black text-white hover:bg-black px-4 py-2 rounded-md"
              >
                Save Changes
              </button>

              <button
                onClick={() => handleDeleteRoom(selectedRoom.roomnumber)}
                className="px-5 py-2 bg-red-500 text-white rounded-md hover:text-black"
              >
                Delete Room
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="top-right" autoClose={3000} hideProgressBar className="mt-12" />
    </div>
  );
};

export default RoomManagement;
